import { requestNotificationPermission } from '../../messaging';

import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../AppContext';
import { httpsCallable } from 'firebase/functions';
import LogViewerModal from '../LogViewerModal'; // Assuming this component exists

const NOTIFICATION_TYPES = [
    { id: 'newConnections', label: 'New Connections' },
    { id: 'newMessages', label: 'New Messages' },
    { id: 'newComments', label: 'New Comments' },
    { id: 'nexusUpdates', label: 'Nexus Updates' },
];

const NotificationsView = () => {
    const { currentUserProfile, updateUserProfile, appFunctions, userId } = useAppContext();
    const [settings, setSettings] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [logContent, setLogContent] = useState('');

    const handleEnableNotifications = () => {
        if (userId) {
            requestNotificationPermission(userId);
        } else {
            alert("You must be logged in to enable notifications.");
        }
    };

    useEffect(() => {
        if (currentUserProfile?.notificationSettings) {
            setSettings(currentUserProfile.notificationSettings);
        } else {
            const defaultSettings = NOTIFICATION_TYPES.reduce((acc, type) => {
                acc[type.id] = true;
                return acc;
            }, {});
            setSettings(defaultSettings);
        }
        setIsLoading(false);
    }, [currentUserProfile]);

    const handleToggle = (settingId) => {
        const newSettings = {
            ...settings,
            [settingId]: !settings[settingId],
        };
        setSettings(newSettings);
        updateUserProfile(currentUserProfile.id, { notificationSettings: newSettings });
    };

    const handleSendTestNotification = async () => {
        const sendTest = httpsCallable(appFunctions, 'sendTestNotification');
        try {
            await sendTest();
        } catch (error) {
            console.error("Error sending test notification:", error);
        }
    };

    const handleTestFCM = async () => {
        const testFCM = httpsCallable(appFunctions, 'testFCM');
        try {
            const result = await testFCM();
            console.log("FCM test function returned:", result);
            alert("FCM test function executed successfully! Check logs and device.");
        } catch (error) {
            console.error("Error calling testFCM function:", error);
            alert(`Error calling testFCM: ${error.message}`);
        }
    };

    const handleViewLog = async () => {
        // In a real app, you'd fetch this from a secure backend endpoint.
        // For demonstration, we'll try to fetch it if it's in the public folder,
        // or show an alert if it's not accessible.
        try {
            const response = await fetch('/firestore-debug.log');
            if (response.ok) {
                const text = await response.text();
                setLogContent(text);
                setIsLogModalOpen(true);
            } else {
                alert("Could not fetch log file. Ensure it's in the public directory for this demo.");
            }
        } catch (error) {
            alert("Error fetching log file.");
        }
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="bg-black/20 backdrop-blur-md rounded-2xl p-6 border border-slate-700/50">
            <h2 className="text-3xl font-bold font-playfair mb-2 text-blue-300">Push Notifications</h2>
            <p className="mb-6 text-gray-400">Control what alerts you receive on your device.</p>

            <div className="mb-6">
                <button onClick={handleEnableNotifications} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-lg transition duration-300">
                    Enable Notifications on This Device
                </button>
            </div>

            <div className="space-y-4">
                {NOTIFICATION_TYPES.map((type) => (
                    <div key={type.id} className="flex items-center justify-between bg-slate-800/50 p-4 rounded-lg border border-slate-700/50">
                        <span className="font-medium">{type.label}</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={settings[type.id] || false}
                                onChange={() => handleToggle(type.id)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-4">
                <button onClick={handleSendTestNotification} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300">
                    Send Test Notification
                </button>
                <button onClick={handleTestFCM} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300">
                    Test FCM Function
                </button>
                <button onClick={handleViewLog} className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition duration-300">
                    View Full Log
                </button>
            </div>

            {isLogModalOpen && <LogViewerModal content={logContent} onClose={() => setIsLogModalOpen(false)} />}
        </div>
    );
};

export default NotificationsView;